<template>
  <div class="user-profile-menu">
    <!-- Version Label -->
    <div
      v-if="showVersionLabel"
      class="label-version mr-2">
      {{ versioning != null && versioning != "" ? "Version" : "" }}
      {{ "R" + versioning }}
    </div>

    <!-- Language Selection Menu -->
    <v-menu v-if="showLanguageMenu" location="bottom">
      <template v-slot:activator="{ props }">
        <v-btn
          icon
          variant="text"
          size="small"
          v-bind="props"
          class="mr-4 flag-button"
          style="background-color: transparent;">
          <flag :iso="currentflag" class="flag-full-circle" />
        </v-btn>
      </template>
      <v-list style="border-radius: 15px;">
        <v-list-item v-for="entry in languages" :key="entry.title"
          @click="changeLocale(entry.language, entry.flag)">
          <template v-slot:prepend>
            <flag :iso="entry.flag" v-bind:squared="false" />
          </template>
          <v-list-item-title class="ml-3">{{ entry.title }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <!-- User Profile Menu -->
    <v-menu location="bottom" :close-on-content-click="false"
      transition="slide-y-transition"
      :offset="10" rounded="lg" min-width="220">
      <template v-slot:activator="{ props }">
        <v-btn
          id="button-username-navbar"
          v-bind="props"
          class="user-profile-button"
          elevation="1"
          variant="text"
          :ripple="false">
          <div class="d-flex align-center">
            <v-avatar size="28" class="mr-2" color="#f3f3f3" variant="flat">
              <span
                class="text-caption font-weight-medium"
                :style="`color: #252525`"
                >
                {{
                  username ? username.substring(0, 1).toUpperCase() : "U"
                }}
              </span>
            </v-avatar>
            <span
              class="user-name"
              :style="`color: #252525`">
              {{ username }}
            </span>
            <v-icon
              size="small"
              :color="navbarTxtColorTheme || 'FFFFFF'"
              class="ml-1">
              mdi-chevron-down
            </v-icon>
          </div>
        </v-btn>
      </template>

      <v-card class="user-menu-card" elevation="3">
        <!-- User profile header -->
        <v-card-item class="user-menu-header pa-4">
          <template v-slot:prepend>
            <v-avatar
              size="42"
              :color="navbarBtnColorTheme || '5179ef'"
              class="mr-3">
              <span class="text-h6 font-weight-medium white--text">
                {{ shotName }}
              </span>
            </v-avatar>
          </template>

          <v-card-title class="pa-0">
            <div>
              <div class="text-body-1 font-weight-medium mb-0">
                {{ fullName }}
              </div>
              <div class="text-caption text-medium-emphasis">
                {{ role }}
              </div>
            </div>
          </v-card-title>
        </v-card-item>

        <v-divider></v-divider>

        <!-- Menu items -->
        <v-list class="pa-2" nav density="comfortable">
          <!-- Sound Toggle Menu Item -->
          <v-list-item 
            v-if="showSoundToggle"
            @click="toggleSound"
            class="menu-list-item mb-1 rounded" 
            :ripple="true">
            <template v-slot:prepend>
              <v-icon
                color="primary"
                size="small"
                class="mr-2">
                {{ soundEnabled ? 'mdi-volume-high' : 'mdi-volume-off' }}
              </v-icon>
            </template>
            <v-list-item-title class="menu-item-title">
              {{ localize('notificationSound') }}
            </v-list-item-title>
            <template v-slot:append>
              <v-switch
                :model-value="soundEnabled"
                @click.stop
                @update:model-value="toggleSound"
                color="primary"
                hide-details
                density="compact"
                inset
                class="ml-2">
              </v-switch>
            </template>
          </v-list-item>

          <v-list-item v-for="v in ddProfile" :key="v.no" :value="v.title" @click="v.action" :to="v.route"
            class="menu-list-item mb-1 rounded" :ripple="true">
            <template v-slot:prepend>
              <v-icon
                :color="v.no === 4 ? 'error' : 'primary'"
                size="small"
                class="mr-2">{{ v.icon }}</v-icon>
            </template>
            <v-list-item-title class="menu-item-title" :class="{ 'text-error': v.no === 4 }">
              {{ localize(v.title) }}
            </v-list-item-title>
          </v-list-item>
        </v-list>

        <v-card-text class="pt-2 pb-3 px-4 text-center">
          <div class="text-caption text-medium-emphasis">
            {{ "Version R" + versioning }}
          </div>
        </v-card-text>
      </v-card>
    </v-menu>

    <!-- Snippet Username Display -->
    <span v-if="snippetUsername != ''" style="
        color: black;
        padding-top: 4px;
        text-align: center;
        font-weight: bold;
        height: 25px;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
      ">
      {{ snippetUsername }}
    </span>
  </div>
</template>

<script>
import { useI18n } from 'vue-i18n';

export default {
  name: 'UserProfileMenu',
  setup() {
    const { t } = useI18n();
    return { t };
  },
  props: {
    // Required props
    username: {
      type: String,
      required: true
    },
    fullName: {
      type: String,
      required: true
    },
    shotName: {
      type: String,
      required: true
    },
    role: {
      type: String,
      required: true
    },
    versioning: {
      type: String,
      required: true
    },
    ddProfile: {
      type: Array,
      required: true
    },
    currentflag: {
      type: String,
      required: true
    },
    languages: {
      type: Array,
      required: true
    },
    soundEnabled: {
      type: Boolean,
      default: false
    },
    
    // Optional props with defaults
    navbarTxtColorTheme: {
      type: String,
      default: 'FFFFFF'
    },
    navbarBtnColorTheme: {
      type: String,
      default: '5179ef'
    },
    snippetUsername: {
      type: String,
      default: ''
    },
    
    // Display control props
    showVersionLabel: {
      type: Boolean,
      default: true
    },
    showLanguageMenu: {
      type: Boolean,
      default: true
    },
    showSoundToggle: {
      type: Boolean,
      default: true
    },
    
    // Localize function prop
    localizeFunction: {
      type: Function,
      default: null
    }
  },

  emits: ['change-locale', 'toggle-sound'],

  methods: {
    changeLocale(language, flag) {
      this.$emit('change-locale', { language, flag });
    },
    
    toggleSound() {
      this.$emit('toggle-sound');
    },
    
    localize(text) {
      // Use passed localize function prop first
      if (this.localizeFunction && typeof this.localizeFunction === 'function') {
        return this.localizeFunction(text);
      }
      // Try to use parent's localize method
      if (this.$parent && typeof this.$parent.localize === 'function') {
        return this.$parent.localize(text);
      }
      // Use composition API's t function
      if (this.t && typeof this.t === 'function') {
        try {
          return this.t(text);
        } catch (error) {
          console.warn('Translation error for text:', text, error);
          return text;
        }
      }
      // Try to use $t method from i18n as fallback
      if (this.$t && typeof this.$t === 'function') {
        try {
          return this.$t(text);
        } catch (error) {
          console.warn('Translation error for text:', text, error);
          return text;
        }
      }
      // Fallback to original text
      return text;
    }
  }
}
</script>

<style scoped>
.user-profile-menu {
  display: flex;
  align-items: center;
}

.user-profile-button {
  transition: all 0.2s ease;
  box-shadow: none !important;
  padding: 0 12px !important;
}

.user-profile-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Flag button styling */
.flag-button {
  overflow: hidden !important;
  padding: 0 !important;
  min-width: 26px !important;
  min-height: 26px !important;
}

.flag-button :deep(.v-btn__content) {
  width: 100% !important;
  height: 100% !important;
  padding: 0 !important;
  margin: 0 !important;
}

/* Flag full circle styling */
.flag-full-circle {
  width: 26px !important;
  height: 26px !important;
  border-radius: 50% !important;
  object-fit: cover !important;
  display: block !important;
}

.flag-full-circle :deep(img) {
  width: 26px !important;
  height: 26px !important;
  border-radius: 50% !important;
  object-fit: cover !important;
  display: block !important;
}

.flag-full-circle :deep(.flag-icon) {
  width: 26px !important;
  height: 26px !important;
  border-radius: 50% !important;
  object-fit: cover !important;
}

.user-menu-card {
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
}

.user-menu-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px 12px 0 0;
}

.menu-list-item {
  transition: all 0.2s ease;
  margin: 2px 0;
}

.menu-list-item:hover {
  background-color: rgba(81, 121, 239, 0.08);
  transform: translateX(4px);
}

.menu-item-title {
  font-weight: 500;
}

.text-error {
  color: #d32f2f !important;
}

.user-name {
  font-weight: 500;
  font-size: 14px;
}

/* Sound toggle switch in menu */
.v-list-item .v-switch {
  transform: scale(0.8);
}

.v-list-item .v-switch .v-switch__track {
  opacity: 1;
}

.v-list-item:hover .v-switch {
  transform: scale(0.85);
  transition: transform 0.2s ease;
}

.label-version {
  display: flex;
  padding: 6px 12px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  color: var(--main, #C32E0E);
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  border-radius: 24px;
  background: linear-gradient(91deg, rgba(246, 119, 96, 0.10) 1.14%, rgba(195, 46, 14, 0.10) 46.51%, rgba(246, 119, 96, 0.10) 120.41%);
}
</style>